import { ConfirmDialog, EmptyState, SkeletonList, StatusChip, isolate } from '@batiflow/ui';
import { Button } from '@fluentui/react-components';
import { Add20Regular, PeopleCommunity24Regular, PersonAdd20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import { isClosed, isStepUpRequired, type Space } from '../model';
import { usePartners, useRemovePartner, useRevokeInvitation, useSpaceInvitations } from './api';
import { InvitePersonDialog } from './invite-person-dialog';
import {
  INVITATION_STATUS_INTENT,
  PARTNER_STATUS_INTENT,
  canRemovePartner,
  dateText,
  isOpenInvitation,
  scopeSummary,
  sharingErrorMessage,
  sortPartners,
  type SpaceInvitation,
  type SpacePartner,
} from './model';
import { ShareDialog } from './share-dialog';
import { LoadState, RowList, SharingRow, SharingSection } from './sharing-ui';

export interface PartnersTabProps {
  space: Space;
  /** The host organisation of the reader (its tax-id lookup is asked in its name). */
  hostOrgId: string | null;
}

/** Plain text of an invitation's kind and recipient: an email or an organisation name. */
function invitationTarget(invitation: SpaceInvitation): string {
  return invitation.email ?? invitation.organizationName ?? '';
}

/**
 * The Partners tab (host managers): the partner organisations with their scope, rights, member
 * count and end date, the invitations still waiting for an answer, and the two ways to share: with
 * an organisation, or with a person.
 */
export function SpacePartnersTab({ space, hostOrgId }: PartnersTabProps) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const locale = useLocale();
  const notify = useNotify();
  const closed = isClosed(space);
  const partners = usePartners(space.id);
  const invitations = useSpaceInvitations(space.id);
  const removePartner = useRemovePartner(space.id);
  const revokeInvitation = useRevokeInvitation(space.id);

  const [sharing, setSharing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<SpacePartner | null>(null);
  const [cancelling, setCancelling] = useState<SpaceInvitation | null>(null);

  const reportError = (error: unknown) => {
    if (isStepUpRequired(error)) return;
    notify({
      intent: 'error',
      message: sharingErrorMessage(t, tCommon, error, { forbidden: 'sharing.errors.forbidden' }),
    });
  };

  const partnerItems = sortPartners(partners.data?.items ?? []);
  // Partnership requests are the "pending" partners above: only people are listed here.
  const personInvitations = (invitations.data?.items ?? []).filter(
    (invitation) => invitation.kind !== 'organization' && isOpenInvitation(invitation),
  );

  return (
    <>
      <SharingSection
        id="partners-list"
        title={t('sharing.partners.heading')}
        description={t('sharing.partners.description')}
        actions={
          !closed && hostOrgId ? (
            <Button
              appearance="primary"
              icon={<Add20Regular />}
              onClick={() => {
                setSharing(true);
              }}
            >
              {t('sharing.partners.share')}
            </Button>
          ) : null
        }
      >
        {partners.isError && !partners.data ? (
          <LoadState
            title={t('sharing.partners.loadFailedTitle')}
            body={t('sharing.loadFailedBody')}
            onRetry={() => {
              void partners.refetch();
            }}
          />
        ) : !partners.data ? (
          <SkeletonList count={3} />
        ) : partnerItems.length === 0 ? (
          <EmptyState
            headingLevel={3}
            illustration={<PeopleCommunity24Regular />}
            title={t('sharing.partners.emptyTitle')}
            description={t('sharing.partners.emptyBody')}
            {...(!closed && hostOrgId
              ? {
                  primaryAction: {
                    label: t('sharing.partners.share'),
                    onClick: () => {
                      setSharing(true);
                    },
                  },
                }
              : {})}
          />
        ) : (
          <RowList label={t('sharing.partners.heading')}>
            {partnerItems.map((partner) => (
              <SharingRow
                key={partner.id}
                title={
                  <>
                    <bdi>{partner.organizationName}</bdi>
                    <StatusChip
                      label={t(`sharing.partnerStatus.${partner.status}`)}
                      intent={PARTNER_STATUS_INTENT[partner.status]}
                    />
                  </>
                }
                details={[
                  scopeSummary(partner.scope, t),
                  t('access.rights', { rights: t(`rights.${partner.rights}`) }),
                  t('sharing.partners.members', {
                    active: partner.activeMembers,
                    cap: partner.memberCap,
                  }),
                  partner.expiresAt
                    ? t('access.until', { date: dateText(partner.expiresAt, locale) })
                    : t('access.noEnd'),
                ]}
                actions={
                  !closed && canRemovePartner(partner) ? (
                    <Button
                      appearance="secondary"
                      aria-label={t('sharing.partners.removeLabel', {
                        name: isolate(partner.organizationName),
                      })}
                      onClick={() => {
                        setRemoving(partner);
                      }}
                    >
                      {t('sharing.remove')}
                    </Button>
                  ) : null
                }
              />
            ))}
          </RowList>
        )}
      </SharingSection>

      <SharingSection
        id="invitations-list"
        title={t('sharing.invitations.heading')}
        description={t('sharing.invitations.description')}
        actions={
          !closed ? (
            <Button
              appearance="secondary"
              icon={<PersonAdd20Regular />}
              onClick={() => {
                setInviting(true);
              }}
            >
              {t('sharing.invitations.invite')}
            </Button>
          ) : null
        }
      >
        {invitations.isError && !invitations.data ? (
          <LoadState
            title={t('sharing.invitations.loadFailedTitle')}
            body={t('sharing.loadFailedBody')}
            onRetry={() => {
              void invitations.refetch();
            }}
          />
        ) : !invitations.data ? (
          <SkeletonList count={3} />
        ) : personInvitations.length === 0 ? (
          <EmptyState
            headingLevel={3}
            illustration={<PersonAdd20Regular />}
            title={t('sharing.invitations.emptyTitle')}
            description={t('sharing.invitations.emptyBody')}
          />
        ) : (
          <RowList label={t('sharing.invitations.heading')}>
            {personInvitations.map((invitation) => (
              <SharingRow
                key={invitation.id}
                title={
                  <>
                    <bdi dir="ltr">{invitationTarget(invitation)}</bdi>
                    <StatusChip
                      label={t(`sharing.invitationStatus.${invitation.status}`)}
                      intent={INVITATION_STATUS_INTENT[invitation.status]}
                    />
                  </>
                }
                details={[
                  t(`sharing.invitations.kind.${invitation.kind}`),
                  scopeSummary(invitation.scope, t),
                  t('access.rights', { rights: t(`rights.${invitation.rights}`) }),
                  t('sharing.invitations.expires', {
                    date: dateText(invitation.expiresAt, locale),
                  }),
                ]}
                actions={
                  !closed ? (
                    <Button
                      appearance="secondary"
                      aria-label={t('sharing.invitations.cancelLabel', {
                        who: isolate(invitationTarget(invitation)),
                      })}
                      onClick={() => {
                        setCancelling(invitation);
                      }}
                    >
                      {t('sharing.invitations.cancel')}
                    </Button>
                  ) : null
                }
              />
            ))}
          </RowList>
        )}
      </SharingSection>

      {hostOrgId ? (
        <ShareDialog
          spaceId={space.id}
          hostOrgId={hostOrgId}
          open={sharing}
          onClose={() => {
            setSharing(false);
          }}
        />
      ) : null}
      <InvitePersonDialog
        spaceId={space.id}
        open={inviting}
        onClose={() => {
          setInviting(false);
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        destructive
        title={t('sharing.partners.removeTitle', {
          name: isolate(removing?.organizationName ?? ''),
        })}
        body={t('sharing.partners.removeBody')}
        confirmLabel={t('sharing.remove')}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        onConfirm={async () => {
          if (!removing) return;
          await removePartner.mutateAsync({ spaceId: space.id, partnerId: removing.id });
          notify({
            intent: 'success',
            message: t('sharing.partners.removed', { name: isolate(removing.organizationName) }),
          });
        }}
        onError={reportError}
      />
      <ConfirmDialog
        open={cancelling !== null}
        destructive
        title={t('sharing.invitations.cancelTitle', {
          who: isolate(cancelling ? invitationTarget(cancelling) : ''),
        })}
        body={t('sharing.invitations.cancelBody')}
        confirmLabel={t('sharing.invitations.cancelConfirm')}
        onOpenChange={(open) => {
          if (!open) setCancelling(null);
        }}
        onConfirm={async () => {
          if (!cancelling) return;
          await revokeInvitation.mutateAsync({ spaceId: space.id, invitationId: cancelling.id });
          notify({ intent: 'success', message: t('sharing.invitations.cancelled') });
        }}
        onError={reportError}
      />
    </>
  );
}
